import type { SessionService } from '@efdigital/ll-addon-auth';
import type { UserGroup } from '@efdigital/ll-addon-data';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import {
  buildGetUsersByUserId,
  buildSearchUsersByEmail,
  buildSearchUsersByText,
  isValidEmail,
  type UserInfo,
} from '@efdigital/ll-admin-v2/data/builders/system/users';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import { isDummyUser } from '@efdigital/ll-admin-v2/utils/user-groups';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { Validation } from '../validation.gts';

export const pendingPrefix = '--pending||';
export const pendingRegex = /^--pending\|\|/;

export const toPending = (email: string) =>
  `${pendingPrefix}${email.toLowerCase()}`;
export const fromPending = (pendingId: string) =>
  pendingId.replace(pendingRegex, '');
export const isPending = (userId: string) => pendingRegex.test(userId);

function eq(a: string, b: string) {
  return a === b;
}

export class EditUserGroupUsers extends Component<
  EditRecordSignature<
    UserGroup,
    {
      filename: string;
      editLabel: string;
      legend: string;
      helpText?: string;
      save: (item: UserGroup) => Promise<void>;
    }
  >
> {
  @service declare session: SessionService;
  @service declare store: Store;

  @tracked tab: 'search' | 'bulk' = 'search';
  @tracked addedUsers: UserInfo[] = [];
  @tracked removedUsers: UserInfo[] = [];
  @tracked bulkValue = '';
  @tracked searchValue = '';
  @tracked searchResults: UserInfo[] | null = null;
  @tracked searchError: string | null = null;

  setTab = (tab: 'search' | 'bulk') => {
    this.tab = tab;
  };

  getUsers = async () => {
    const request = await buildGetUsersByUserId(
      {
        includeDeleted: true,
        id: (this.args.item.userIds || []).filter((id) => !isPending(id)) || [],
      },
      this.session
    );

    const response = await fetch(request);
    let data: UserInfo[] = [];
    if (response.ok) {
      data = (await response.json()) as UserInfo[];
    } else {
      console.error('Failed to get users: ', response.statusText);
    }

    return (this.args.item.userIds || [])
      .filter((userId) => !isDummyUser(userId))
      .map((userId) =>
        isPending(userId)
          ? {
              id: userId,
              title: fromPending(userId),
              lastLogin: '',
              created: '',
            }
          : data.find((user) => user.id === userId) || {
              id: userId,
              lastLogin: '',
              created: '',
              title: `Unknown user ID: ${userId}`,
            }
      );
  };

  validate = () => {
    if (!this.mergedUserIds.length) {
      return Validation.fromFailures([
        {
          message:
            'There must be at least one user in the group (pending or registered)',
        },
      ]);
    } else {
      return Validation.success();
    }
  };

  sortUsers = (users: UserInfo[]) => {
    return users.sort((a, b) => {
      return a.title.localeCompare(b.title);
    });
  };

  resolveSearch = async () => {
    this.searchError = null;

    const searchText = this.searchValue.trim() || '';
    if (!searchText) {
      this.searchError = 'Please enter a search term';
      return;
    }

    const request = await buildSearchUsersByText(
      {
        text: searchText,
      },
      this.session
    );

    const response = await fetch(request);
    if (!response.ok) {
      this.searchError = `Failed to find users: ${response.statusText}`;
      return;
    }

    const results = (await response.json()) as UserInfo[];
    if (results.length) {
      this.searchResults = results;
    } else if (isValidEmail(searchText)) {
      this.searchResults = [
        {
          id: toPending(searchText.toLowerCase()),
          title: searchText.toLowerCase(),
          lastLogin: '',
        },
      ];
    } else {
      this.searchResults = [];
      this.searchError = 'No users found matching the search criteria';
    }
  };

  resolveBulkUsers = async () => {
    this.searchError = null;

    if (!this.bulkValue) {
      this.searchError = 'Please enter one or more e-mail addresses';
      return;
    }

    const emails = this.bulkValue
      .split(/\s+/)
      .filter((f) => f)
      .filter((f) => isValidEmail(f));

    if (!emails.length) {
      this.searchError = 'Please enter one or more valid e-mail addresses';
      return;
    }

    const request = await buildSearchUsersByEmail(
      {
        emails,
      },
      this.session
    );

    const response = await fetch(request);
    if (!response.ok) {
      this.searchError = `Failed to find users: ${response.statusText}`;
      return;
    }
    const searchResults = (await response.json()) as UserInfo[];

    const usersToAdd = emails.map((text) => {
      const textLower = text.toLowerCase();
      const existing = searchResults.find(
        (i) => i.title.toLowerCase() === textLower
      );
      if (existing) {
        return existing;
      }

      const placeholderUser: UserInfo = {
        id: toPending(textLower),
        title: textLower,
        lastLogin: '',
      };

      return placeholderUser;
    });

    this.addedUsers = [
      ...this.addedUsers,
      ...usersToAdd.filter(
        (u) =>
          !this.addedUsers.find((a) => a.id === u.id) &&
          !(this.args.item.userIds || []).find((id) => id === u.id)
      ),
    ];

    this.removedUsers = this.removedUsers.filter(
      (u) => !usersToAdd.find((a) => a.id === u.id)
    );

    this.bulkValue = '';
    this.searchError = null;
  };

  removeUser = (user: UserInfo) => {
    if (this.addedUsers.find((u) => u.id === user.id)) {
      this.addedUsers = this.addedUsers.filter((u) => u.id !== user.id);
    }
    if ((this.args.item.userIds || []).find((id) => id === user.id)) {
      this.removedUsers = Array.from(new Set([...this.removedUsers, user]));
    }
  };

  restoreUser = (user: UserInfo) => {
    if (this.removedUsers.find((u) => u.id === user.id)) {
      this.removedUsers = this.removedUsers.filter((u) => u.id !== user.id);
    }
  };

  isRemoved = (user: UserInfo) => {
    if (isDummyUser(user.id)) {
      return true;
    }
    return this.removedUsers.find((u) => u.id === user.id);
  };

  updateSearchValue = (event: Event) => {
    const textinput = event.target as HTMLInputElement;
    this.searchValue = textinput.value;
  };

  updateBulkValue = (event: Event) => {
    const textarea = event.target as HTMLTextAreaElement;
    this.bulkValue = textarea.value;
  };

  preventEnterSubmit = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.updateSearchValue(event);
      void this.resolveSearch();
    }
  };

  addUser = (user: UserInfo) => {
    // If the user is currently marked as removed, unmark them instead of re-adding
    if (this.removedUsers.find((u) => u.id === user.id)) {
      this.removedUsers = this.removedUsers.filter((u) => u.id !== user.id);
      return;
    }

    // If the user is already part of the group, do nothing
    if (this.args.item.userIds?.includes(user.id)) {
      return;
    }

    // If the user is already in the list of newly added users, do nothing
    if (this.addedUsers.find((u) => u.id === user.id)) {
      return;
    }
    this.addedUsers = [...this.addedUsers, user];
  };

  saveUsers = () => {
    const realUsers = this.mergedUserIds.filter((id) => !isDummyUser(id));
    // Strip dummy placeholder users when real users exist to avoid API domain
    // validation errors. Keep dummies only for empty groups (API requires non-empty).
    this.args.item.userIds =
      realUsers.length > 0 ? realUsers : this.mergedUserIds;
    this.removedUsers = [];
    this.addedUsers = [];
    return this.args.save(this.args.item);
  };

  revertUsers = () => {
    this.addedUsers = [];
    this.removedUsers = [];
    return deepRollback(this.args.item, this.store, {
      preventNewRecordUnload: true,
    });
  };

  get mergedUserIds() {
    return [
      ...(this.args.item.userIds || []).filter(
        (id) => isDummyUser(id) || !this.removedUsers.find((u) => u.id === id)
      ),
      ...this.addedUsers.map((u) => u.id),
    ];
  }

  escapeCsvValue = (value: string): string => {
    // Determine if the original value needs quoting
    const needsQuoting =
      value.includes(',') || value.includes('"') || value.includes('\n');
    let safeValue = value;
    // Prevent CSV injection by prefixing values that start with formula characters
    if (safeValue.match(/^[=+\-@]/)) {
      safeValue = "'" + safeValue;
    }
    // Escape quotes and wrap in quotes if value contains comma, quote, or newline
    if (needsQuoting) {
      safeValue = '"' + safeValue.replace(/"/g, '""') + '"';
    }
    return safeValue;
  };

  downloadCSV = async () => {
    let url: string | null = null;
    try {
      const users = await this.getUsers();

      if (!users || users.length === 0) {
        return;
      }

      const csv = [
        [
          this.escapeCsvValue('Email'),
          this.escapeCsvValue('Status'),
          this.escapeCsvValue('ID'),
        ].join(','),
        ...users.map((u) =>
          [
            this.escapeCsvValue(u.title),
            this.escapeCsvValue(isPending(u.id) ? 'Pending' : 'Registered'),
            this.escapeCsvValue(isPending(u.id) ? '' : u.id),
          ].join(',')
        ),
      ].join('\n');

      const csvWithBom = '\uFEFF' + csv;
      const blob = new Blob([csvWithBom], { type: 'text/csv' });
      url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const rawGroupName = (this.args.item && this.args.item.title) || '';
      const safeGroupName = rawGroupName
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '');
      const filename = safeGroupName
        ? `${this.args.filename}-${safeGroupName}.csv`
        : `${this.args.filename}.csv`;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        if (url) {
          URL.revokeObjectURL(url);
        }
        a.remove();
      }, 0);
    } catch (error) {
      console.error('Failed to export CSV', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  <template>
    <EditRecord
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @editLabel={{@editLabel}}
      @validate={{this.validate}}
      @save={{this.saveUsers}}
      @revert={{this.revertUsers}}
      @maxDepth={{1}}
    >
      <:form as |uniqueId isReadOnly|>
        <Await @promise={{(this.getUsers)}}>
          <:success as |users|>
            <fieldset disabled={{isReadOnly}} class="mb-4">
              <legend class="gud-eyebrow">{{@legend}}</legend>

              {{#if @helpText}}
                <p>{{@helpText}}</p>
              {{/if}}

              <div class="list-group mb-3">
                {{#each (this.sortUsers users) as |userInfo|}}
                  {{#let (this.isRemoved userInfo) as |isRemoved|}}
                    <div
                      class="list-group-item
                        {{if isRemoved 'list-group-item-danger'}}"
                    >
                      <div
                        class="d-flex justify-content-start align-items-top gap-3"
                      >
                        <div class="flex-shrink-0 flex-grow-0">
                          {{#if isRemoved}}
                            <iconify-icon
                              inline
                              icon="ph:user-fill"
                              class="text-danger"
                            />
                          {{else}}
                            <iconify-icon inline icon="ph:user-bold" />
                          {{/if}}
                        </div>
                        <div
                          class="flex-grow-1
                            {{if isRemoved 'text-decoration-line-through'}}"
                        >
                          {{userInfo.title}}
                          {{#if (isPending userInfo.id)}}
                            <span class="badge text-bg-dark">Pending</span>
                          {{/if}}
                        </div>
                        {{#unless isReadOnly}}
                          {{#if isRemoved}}
                            <button
                              type="button"
                              class="btn btn-outline-danger btn-sm m-0 py-0"
                              {{on "click" (fn this.restoreUser userInfo)}}
                            >
                              <iconify-icon inline icon="ph:plus-bold" />
                              <span class="visually-hidden">Restore user</span>
                            </button>
                          {{else}}
                            <button
                              type="button"
                              class="btn btn-outline-danger btn-sm m-0 py-0"
                              {{on "click" (fn this.removeUser userInfo)}}
                            >
                              <iconify-icon inline icon="ph:minus-bold" />
                              <span class="visually-hidden">Remove user</span>
                            </button>
                          {{/if}}
                        {{/unless}}
                      </div>
                    </div>
                  {{/let}}
                {{/each}}
                {{#each (this.sortUsers this.addedUsers) as |userInfo|}}
                  <div class="list-group-item list-group-item-success">
                    <div
                      class="d-flex justify-content-start align-items-top gap-3"
                    >
                      <div class="flex-shrink-0 flex-grow-0">
                        <iconify-icon
                          inline
                          icon="ph:user-fill"
                          class="text-success"
                        />
                      </div>
                      <div class="flex-grow-1">
                        {{userInfo.title}}
                        {{#if (isPending userInfo.id)}}
                          <span class="badge text-bg-dark">Pending</span>
                        {{/if}}
                      </div>
                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm m-0 py-0"
                        {{on "click" (fn this.removeUser userInfo)}}
                      >
                        <iconify-icon inline icon="ph:x-bold" />
                        <span class="visually-hidden">Remove user</span>
                      </button>
                    </div>
                  </div>
                {{/each}}
              </div>

              {{#if isReadOnly}}
                {{#unless users.length}}
                  <p>No users have been assigned.</p>
                {{/unless}}
              {{/if}}

              {{#unless isReadOnly}}
                <div class="card bg-secondary-subtle">
                  <div class="card-body">
                    <nav class="nav nav-pills justify-content-center mb-3">
                      <div class="nav-item bg-white rounded-start-1">
                        <button
                          class="nav-link fw-bold
                            {{if (eq this.tab 'search') 'active'}}"
                          type="button"
                          {{on "click" (fn this.setTab "search")}}
                        >Search For User</button>
                      </div>
                      <div class="nav-item bg-white rounded-end-1">
                        <button
                          class="nav-link fw-bold
                            {{if (eq this.tab 'bulk') 'active'}}"
                          type="button"
                          {{on "click" (fn this.setTab "bulk")}}
                        >Bulk Input</button>
                      </div>
                    </nav>
                    {{#if (eq this.tab "search")}}
                      <div class="mb-3">
                        <label
                          for="{{uniqueId}}-searchInput"
                          class="form-label mb-1 visually-hidden"
                        >Search for a user</label>
                        <p class="form-text">
                          Enter email address, user ID or search text.
                        </p>
                        <input
                          type="text"
                          class="form-control"
                          id="{{uniqueId}}-searchInput"
                          placeholder="Enter search text"
                          value={{this.searchValue}}
                          {{on "change" this.updateSearchValue}}
                          {{on "keydown" this.preventEnterSubmit}}
                        />
                      </div>
                      <div class="text-center">
                        <button
                          type="button"
                          class="btn btn-secondary btn-md mb-3"
                          {{on "click" this.resolveSearch}}
                        >
                          Search
                        </button>
                      </div>
                      {{#if this.searchResults.length}}
                        <div
                          class="list-group ll-user-group-users-search-results mb-3"
                        >
                          {{#each this.searchResults as |userInfo|}}
                            <button
                              class="list-group-item list-group-item-action"
                              type="button"
                              {{on "click" (fn this.addUser userInfo)}}
                            >
                              {{userInfo.title}}
                              {{#if (isPending userInfo.id)}}
                                <span class="badge text-bg-dark">Pending</span>
                              {{/if}}
                            </button>
                          {{/each}}
                        </div>
                      {{/if}}
                    {{/if}}
                    {{#if (eq this.tab "bulk")}}
                      <div class="mb-3">
                        <label
                          for="{{uniqueId}}-addUserInput"
                          class="form-label mb-1 visually-hidden"
                        >Add users by email address</label>
                        <p class="form-text">
                          Enter one or more e-mail addresses, separated by
                          spaces or new lines.
                        </p>
                        <textarea
                          class="form-control"
                          id="{{uniqueId}}-addUserInput"
                          placeholder="Enter user emails"
                          rows="4"
                          value={{this.bulkValue}}
                          {{on "change" this.updateBulkValue}}
                        />
                      </div>
                      <div class="text-center">
                        <button
                          type="button"
                          class="btn btn-secondary btn-md mb-3"
                          {{on "click" this.resolveBulkUsers}}
                        >
                          Add Users
                        </button>
                      </div>
                    {{/if}}

                    {{#if this.searchError}}
                      <div class="alert alert-danger" role="alert">
                        {{this.searchError}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              {{/unless}}
            </fieldset>
          </:success>
        </Await>
      </:form>

      <:buttons as |uniqueId isReadOnly|>
        {{#if isReadOnly}}
          <button
            id="{{uniqueId}}-download"
            type="button"
            class="btn btn-outline-secondary btn-md"
            {{on "click" this.downloadCSV}}
          >
            <iconify-icon inline icon="ph:download-bold" />
            Export CSV
          </button>
        {{/if}}
      </:buttons>
    </EditRecord>
  </template>
}
